// Import a map bundle from a dropped / picked zip. Loads the zip's map.json
// into the editor's in-memory state as unsaved edits. Any files under
// `assets/` are registered into EditorAssets and the JSON's `assets/<name>`
// references are rewritten to `asset://<id>` so subsequent loads / playtest
// resolve them via the session blob URLs.

import JSZip from 'jszip';
import { validateMap, isMaterialPreset, isMaterialModule, type MapJSON, type ObjectNode } from '../../maps/MapJSON';
import { getBundle } from '../../maps/registry';
import { registerAssetFromBytes, makeAssetUrl, type AssetKind } from './EditorAssets';

const INLINE_MAP_KEYS = ['mapColor', 'mapNormal', 'mapRoughness', 'mapMetalness', 'mapAo', 'mapEmissive'] as const;

export interface ImportResult {
  json: MapJSON;
  bundleId: string;
  hasBaseBundle: boolean;
}

export async function importBundleFromFile(file: File): Promise<ImportResult> {
  const zip = await JSZip.loadAsync(file);
  const mapJsonEntry = zip.file('map.json');
  if (!mapJsonEntry) {
    throw new Error('Zip is missing map.json at the root');
  }
  const raw = await mapJsonEntry.async('string');
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`map.json parse failed: ${(err as Error).message}`);
  }
  validateMap(parsed);
  const json = parsed as MapJSON;

  // Register any asset files under assets/* with EditorAssets and build a
  // name → asset:// url map.
  const nameToUrl = new Map<string, string>();
  const assetsDir = zip.folder('assets');
  if (assetsDir) {
    const promises: Promise<void>[] = [];
    assetsDir.forEach((relativePath, entry) => {
      if (entry.dir) return;
      const name = relativePath.split('/').pop() ?? relativePath;
      const kind = kindFromName(name);
      if (!kind) return;
      promises.push((async () => {
        const u8 = new Uint8Array(await entry.async('arraybuffer'));
        const a = registerAssetFromBytes(u8, name, kind);
        nameToUrl.set(`assets/${name}`, makeAssetUrl(a.id));
      })());
    });
    await Promise.all(promises);
  }

  // Rewrite any `assets/<name>` references in the JSON to `asset://<id>`.
  const rewrite = (s: string | undefined): string | undefined => s ? (nameToUrl.get(s) ?? s) : s;
  if (json.ibl?.hdrPath) json.ibl = { ...json.ibl, hdrPath: rewrite(json.ibl.hdrPath) };
  if (json.pickerCard?.thumbnail) {
    json.pickerCard = { ...json.pickerCard, thumbnail: rewrite(json.pickerCard.thumbnail) ?? '' };
  }
  const walk = (nodes: ObjectNode[] | undefined): void => {
    if (!nodes) return;
    for (const n of nodes) {
      if (n.type === 'modelRef') (n as { src: string }).src = rewrite(n.src) ?? n.src;
      const m = n.material as Record<string, unknown> | undefined;
      if (m && !isMaterialPreset(n.material) && !isMaterialModule(n.material)) {
        for (const key of INLINE_MAP_KEYS) {
          const cur = m[key];
          if (typeof cur === 'string') {
            const next = rewrite(cur);
            if (next) m[key] = next;
          }
        }
      }
      if (n.type === 'group') walk(n.children);
    }
  };
  walk(json.objects);

  return {
    json,
    bundleId: json.id,
    hasBaseBundle: !!getBundle(json.id)
  };
}

function kindFromName(name: string): AssetKind | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.glb') || lower.endsWith('.gltf')) return 'glb';
  if (lower.endsWith('.hdr')) return 'hdr';
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'png';
  return null;
}
